import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Conversation } from '@twilio/conversations';
import { ItemUser } from 'data/firestore/globals';
import useHttpMutation from 'data/firestore/useHttpMutation';
import { EntityGet } from 'data/firestore/types';
import { Optout } from 'data/firestore/agent/optout/types';
import useTwilioConversation from '../../../hooks/useTwilioConversation';
import useTwilioQueryKey from './useTwilioQueryKey';

interface InitConversationRequest {
    phoneNumber: string;
}

interface InitConversationResponse {
    conversationSid: string;
    participants: Record<string, ItemUser>;
    optOut: EntityGet<Optout>;
}

interface HookResult {
    conversation: Conversation;
    participants: Record<string, ItemUser | ItemUser[]>;
    optOut: EntityGet<Optout>;
}

const useConversation = (phoneNumber?: string) => {
    const { agentId = '' } = useParams();
    const { client } = useTwilioConversation();
    const key = useTwilioQueryKey(['conversation', phoneNumber], client);

    const {
        mutateAsync: initConversation,
    } = useHttpMutation<InitConversationRequest, InitConversationResponse>({
        pathname: `/agents/${agentId}/conversations/init`,
        method: 'POST',
    });
    return useQuery<unknown, unknown, HookResult>(
        key,
        async () => {
            if (!phoneNumber || !agentId) return undefined;
            const { conversationSid, participants, optOut } = await initConversation({
                body: { phoneNumber },
            });
            const conversation = await client?.peekConversationBySid(conversationSid);
            return {
                conversation,
                participants,
                optOut,
            };
        },
        {
            cacheTime: 0,
        },
    );
};

export default useConversation;
